import React from 'react';
import { humanColumnName } from 'src/components/star-grid/datasets/utils/datasetColumns';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import Table, { type TableProps } from '@amzn/awsui-components-core/table';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import type { ColumnRole, DatasetLookup, ProfileColumn } from 'src/components/star-grid/datasets/datasetTypes';
import { effectiveHierarchyChoice } from 'src/components/star-grid/datasets/utils/datasetHierarchy';
import { candidateChecks } from 'src/components/star-grid/datasets/utils/lookupChecks';
import { useHierarchyChooser } from 'src/components/star-grid/datasets/hooks/useHierarchyChooser';
import { SuggestionBadge } from 'src/components/star-grid/datasets/components/SuggestionBadge';
import { ColumnHierarchySelect } from 'src/components/star-grid/datasets/components/ColumnHierarchySelect';
import { HierarchyChooserModal } from 'src/components/star-grid/datasets/components/HierarchyChooserModal';
import { LookupChecks } from 'src/components/star-grid/datasets/components/LookupChecks';
import { LookupEditorModal } from 'src/components/star-grid/datasets/components/LookupEditorModal';
import { WizardReportFeatures } from 'src/components/star-grid/datasets/components/WizardReportFeatures';
import { WizardCalculatedMeasures } from 'src/components/star-grid/datasets/components/WizardCalculatedMeasures';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';
import { effectiveRole } from 'src/components/star-grid/datasets/utils/datasetDraft';

const ROLE_ORDER: readonly ColumnRole[] = ['measure', 'time', 'scenario', 'dimension', 'currency', 'ignore'];
const ROLE_TEXT: Record<ColumnRole, Parameters<StarGridText>[0]> = {
  measure: 'wizardRoleMeasure',
  time: 'wizardRoleTime',
  scenario: 'wizardRoleScenario',
  dimension: 'wizardRoleDimension',
  currency: 'wizardRoleCurrency',
  ignore: 'wizardRoleIgnore'
};

interface LookupEdit {
  readonly column: string;
  readonly existing?: DatasetLookup;
}

/**
 * Roles, Prophecy hierarchies and lookups in one table, every suggestion pre-filled. A lookup is offered
 * only for a dimension with no hierarchy: one that maps to Prophecy already gets its labels and tree there.
 */
export const WizardMapColumnsStep: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const chooser = useHierarchyChooser();
  const [lookupEdit, setLookupEdit] = React.useState<LookupEdit | null>(null);
  if (state.profileLoading || !state.profile) {
    return (
      <div className={styles.body}>
        <Box>
          <Spinner /> {text('wizardColumnsLoading')}
        </Box>
      </div>
    );
  }
  const roleOptions = ROLE_ORDER.map((role) => ({ value: role, label: text(ROLE_TEXT[role]) }));
  const roleOf = (column: ProfileColumn): ColumnRole => effectiveRole(column, state.roles);
  const measureCount = state.profile.columns.filter((column) => roleOf(column) === 'measure').length;
  const hasMeasure = measureCount > 0;

  const lookupCell = (column: ProfileColumn): React.ReactNode => {
    const accepted = state.lookups.find((lookup) => lookup.column === column.name);
    if (accepted) {
      return (
        <SpaceBetween size="xxs" data-testid={`lookup-accepted-${column.name}`}>
          <span className={styles.wrapAnywhere}>
            {accepted.table_ref} · {accepted.key}
          </span>
          {state.lookupChecks[column.name] ? (
            <LookupChecks text={text} checks={state.lookupChecks[column.name]} />
          ) : (
            <span className={styles.samples}>{text('wizardLookupRecheck')}</span>
          )}
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="inline-link"
              data-testid={`lookup-override-${column.name}`}
              onClick={() => setLookupEdit({ column: column.name, existing: accepted })}
            >
              {text('wizardLookupOverride')}
            </Button>
            <Button variant="inline-link" data-testid={`lookup-remove-${column.name}`} onClick={() => state.removeLookup(column.name)}>
              {text('wizardLookupRemove')}
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      );
    }
    if (effectiveHierarchyChoice(column, state.hierarchyChoices).kind === 'hierarchy') return <span className={styles.samples}>—</span>;
    const candidate = state.pendingCandidates.find((entry) => entry.column === column.name);
    return (
      <SpaceBetween size="xxs">
        {candidate && (
          <SpaceBetween size="xxs" data-testid={`lookup-candidate-${column.name}`}>
            <span className={styles.wrapAnywhere}>
              {text('wizardLookupsSuggested')}: {candidate.table_ref} · {candidate.key}
            </span>
            <LookupChecks text={text} checks={candidateChecks(candidate)} />
            <Button variant="inline-link" data-testid={`lookup-accept-${column.name}`} onClick={() => state.acceptCandidate(candidate)}>
              {text('wizardLookupAccept')}
            </Button>
          </SpaceBetween>
        )}
        <Button
          variant="inline-link"
          iconName="add-plus"
          data-testid={`lookup-open-${column.name}`}
          onClick={() => setLookupEdit({ column: column.name })}
        >
          {text('wizardMapAddLookup')}
        </Button>
      </SpaceBetween>
    );
  };

  const columnDefinitions: TableProps.ColumnDefinition<ProfileColumn>[] = [
    {
      id: 'name',
      header: text('wizardColumnName'),
      cell: (column) => (
        <SpaceBetween size="xxs">
          <strong>{column.name}</strong>
          <span className={styles.samples}>{column.type}</span>
        </SpaceBetween>
      )
    },
    { id: 'samples', header: text('wizardColumnSamples'), cell: (column) => <span className={styles.samples}>{column.samples.join(', ')}</span> },
    {
      id: 'role',
      header: text('wizardColumnRole'),
      cell: (column) => (
        <div className={styles.roleCell}>
          <div className={styles.roleSelect}>
            <Select
              expandToViewport
              ariaLabel={`${text('wizardColumnRole')} ${column.name}`}
              data-testid={`wizard-role-${column.name}`}
              options={roleOptions}
              selectedOption={roleOptions.find((option) => option.value === roleOf(column)) ?? null}
              onChange={({ detail }) => state.setRole(column.name, detail.selectedOption.value as ColumnRole)}
            />
          </div>
          <SuggestionBadge text={text} suggestion={text(ROLE_TEXT[column.suggested_role])} />
        </div>
      )
    },
    {
      id: 'label',
      header: <span title={text('wizardColumnLabelHelp')}>{text('wizardColumnLabel')}</span>,
      cell: (column) =>
        roleOf(column) === 'dimension' || roleOf(column) === 'measure' ? (
          <Input
            value={state.labels[column.name] ?? ''}
            placeholder={humanColumnName(column.name)}
            ariaLabel={`${text('wizardColumnLabel')} ${column.name}`}
            onChange={({ detail }) => state.setLabel(column.name, detail.value)}
          />
        ) : (
          <span className={styles.samples}>—</span>
        )
    },
    {
      id: 'hierarchy',
      header: text('wizardMapHierarchy'),
      cell: (column) =>
        roleOf(column) === 'dimension' ? (
          <ColumnHierarchySelect state={state} text={text} chooser={chooser} column={column} />
        ) : (
          <span className={styles.samples}>—</span>
        )
    },
    {
      id: 'lookup',
      header: text('wizardMapLookup'),
      cell: (column) => (roleOf(column) === 'dimension' ? lookupCell(column) : <span className={styles.samples}>—</span>)
    }
  ];
  if (measureCount === 1) {
    columnDefinitions.push({
      id: 'measureDimension',
      header: text('wizardMeasureDimension'),
      cell: (column) =>
        roleOf(column) === 'dimension' ? (
          <Checkbox
            checked={state.measureDimension === column.name}
            ariaLabel={`${text('wizardMeasureDimension')} ${column.name}`}
            data-testid={`measure-dimension-${column.name}`}
            onChange={({ detail }) => state.setMeasureDimension(detail.checked ? column.name : null)}
          />
        ) : null
    });
  }

  return (
    <div className={styles.body}>
      <p className={styles.help}>{text('wizardMapHelp')}</p>
      {!hasMeasure && <Alert type="warning">{text('wizardColumnsNoMeasure')}</Alert>}
      {state.presetReason && (
        <Alert type="info" header={text('wizardPresetApplied')}>
          {state.presetReason}
        </Alert>
      )}
      {measureCount === 1 && <Box color="text-body-secondary">{text('wizardMeasureDimensionHelp')}</Box>}
      <Table variant="embedded" wrapLines items={state.profile.columns} trackBy="name" columnDefinitions={columnDefinitions} />
      <WizardCalculatedMeasures state={state} text={text} />
      <WizardReportFeatures state={state} text={text} />
      {chooser.openFor && (
        <HierarchyChooserModal
          text={text}
          pairs={state.allHierarchies}
          loading={state.hierarchiesLoading}
          onDismiss={chooser.close}
          onPick={(pair) => {
            const column = chooser.openFor as string;
            chooser.remember(column, pair);
            state.setHierarchyChoice(column, { kind: 'hierarchy', dimension: pair.dimension, hierarchy: pair.hierarchy, coverage: null });
            chooser.close();
          }}
        />
      )}
      {lookupEdit && (
        <LookupEditorModal
          state={state}
          text={text}
          column={lookupEdit.column}
          existing={lookupEdit.existing}
          onDismiss={() => setLookupEdit(null)}
        />
      )}
    </div>
  );
};
